import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useCreateBriefInvoice } from "@entities/brief/lib/hooks/useCreateBriefInvoice";
import { useEditBriefInvoice } from "@entities/brief/lib/hooks/useEditBriefInvoice";
import { useGetBriefInvoiceAggregation } from "@entities/brief/lib/hooks/useGetBriefInvoiceAggregation";
import { useGetBriefInvoiceFacets } from "@entities/brief/lib/hooks/useGetBriefInvoiceFacets";
import { useGetBriefInvoices } from "@entities/brief/lib/hooks/useGetBriefInvoices";
import { ToastService } from "@shared/lib/services";
import {
  IBriefInvoice,
  IBriefInvoiceSearchRequest,
  ICreateBriefInvoice,
  IEditBriefInvoice,
  IGetBriefInvoiceAggregation,
  INVOICE_SUB_TYPE,
  INVOICE_TYPE,
} from "@shared/lib/types/briefInvoice";
import { formatData } from "@shared/lib/utils/formatData";
import { Table, TableTitleLayout } from "@shared/ui";
import { Button } from "@shared/ui/Button";
import SelectFilter from "@shared/ui/filters/SelectFilter";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import {
  Card,
  Col,
  DatePicker,
  Divider,
  Flex,
  Input,
  Modal,
  Row,
  Tag
} from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { CreateBriefInvoiceEntry } from "./CreateBriefInvoiceEntry";
import { EditBriefInvoiceEntry } from "./EditBriefInvoiceEntry";

const { RangePicker } = DatePicker;

export function BriefInvoice({ briefId }: { briefId: string }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [tableType, setTableType] = useState<INVOICE_TYPE[]>([]);
  const [tableSubType, setTableSubType] = useState<INVOICE_SUB_TYPE[]>([]);
  const [isUrl, setUrl] = useState<string[]>([]);
  const [billingIds, setBillingIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null]
  >([null, null]);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<IBriefInvoice | null>(
    null
  );

  const searchParams: IBriefInvoiceSearchRequest = {
    briefId,
    searchQuery,
    type: tableType,
    subType: tableSubType,
    billingId: billingIds,
    isUrl,
    createdAtFrom: dateRange[0]?.startOf("day").toISOString() || "",
    createdAtTo: dateRange[1]?.endOf("day").toISOString() || "",
  };

  const { invoices, isFetching, totalCount } =
    useGetBriefInvoices(searchParams);
  const { facets } = useGetBriefInvoiceFacets(briefId);
  const { create, isCreating, isCreated } = useCreateBriefInvoice();
  const { edit, isEditing, isEdited } = useEditBriefInvoice();

  const typeToDisplay = facets?.type.map((item) => item._id);
  const subTypeToDisplay = facets?.subType.map((item) => item._id);
  const urlOptions = ["Yes", "No", "All"];
  const billingIdsToDisplay = facets?.billing.map((item) => ({
    name: item.name,
    value: item._id,
  }));
  const { aggregation } = useGetBriefInvoiceAggregation(briefId);

  useEffect(() => {
    if (isCreated) {
      setCreateModalOpen(false);
      ToastService.success("Invoice created successfully");
    }
  }, [isCreated]);

  useEffect(() => {
    if (isEdited) {
      setEditModalOpen(false);
      setSelectedInvoice(null);
      ToastService.success("Invoice updated successfully");
    }
  }, [isEdited]);

  const handleCreate = (values: ICreateBriefInvoice) => {
    create({
      ...values,
      briefId: briefId,
    });
  };

  const handleEdit = (values: IEditBriefInvoice) => {
    edit(values);
  };

  const handleEditClick = (record: IBriefInvoice) => {
    setSelectedInvoice(record);
    setEditModalOpen(true);
  };

  const handleSelectionChange = (items: string[]) => {
    setTableSubType(items as INVOICE_SUB_TYPE[]);
  };

  const columns: ColumnsType<IBriefInvoice> = [
    {
      title: "Invoice Number",
      key: "invoiceNumber",
      render: (_: string, record: IBriefInvoice) => (
        <div>{record.invoiceNumber || "N/A"}</div>
      ),
    },
    {
      title: "Created At",
      key: "createdAt",
      filterCount: dateRange[0] || dateRange[1] ? 1 : 0,
      filterDropdownProps: { destroyPopupOnHide: true },
      filtered: !!(dateRange[0] || dateRange[1]),
      render: (_: string, record: IBriefInvoice) => (
        <div>{dayjs(record.createdAt).format("DD/MM/YYYY")}</div>
      ),
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <div style={{ padding: 8 }}>
          <RangePicker
            allowEmpty={[true, true]}
            value={dateRange}
            onChange={(dates) => {
              setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null]);
              props.confirm();
            }}
            style={{ marginBottom: 8, width: "100%" }}
            showTime={false}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button
              size="small"
              onClick={() => {
                setDateRange([null, null]);
                props.confirm();
              }}
            >
              Reset
            </Button>
            <Button type="primary" size="small" onClick={props.confirm}>
              OK
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: "Urls",
      key: "url",
      filterCount: isUrl.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filtered: !!isUrl.length,
      render: (_: string, record: IBriefInvoice) => (
        <>
          {record?.url ? (
            <Button
              type="link"
              href={record.url}
              onClick={() => window.open(record.url, "_blank")}
            >
              <TextWithTooltip text={record.url} />
            </Button>
          ) : (
            "N/A"
          )}
        </>
      ),
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Urls"
          props={props}
          placeholder="Select Url Options"
          initialTagCount={3}
          itemsToDisplay={urlOptions}
          selectedItems={isUrl}
          handleSelectionChange={(items: string[]) => setUrl(items)}
        />
      ),
    },
    {
      title: "Billing",
      key: "billing",
      filterCount: billingIds.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filtered: !!billingIds.length,
      render: (_: string, record: IBriefInvoice) => (
        <div>{record.billing.name || "N/A"}</div>
      ),
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Billing"
          props={props}
          placeholder="Select Billing"
          initialTagCount={3}
          itemsToDisplay={billingIdsToDisplay}
          selectedItems={billingIds}
          handleSelectionChange={(items: string[]) => setBillingIds(items)}
        />
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      filterCount: tableType.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filtered: !!tableType.length,
      render: (type: INVOICE_TYPE) => <Tag color="blue">{type}</Tag>,
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Type"
          props={props}
          placeholder="Select Type"
          initialTagCount={3}
          itemsToDisplay={typeToDisplay}
          selectedItems={tableType}
          handleSelectionChange={(items: string[]) =>
            setTableType(items as INVOICE_TYPE[])
          }
        />
      ),
    },
    {
      title: "Sub Type",
      dataIndex: "subType",
      key: "subType",
      filterCount: tableSubType.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filtered: !!tableSubType.length,
      render: (subType: string) => <Tag color="green">{subType}</Tag>,
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Sub Type"
          props={props}
          placeholder="Select Sub Type"
          initialTagCount={3}
          itemsToDisplay={subTypeToDisplay}
          selectedItems={tableSubType}
          handleSelectionChange={handleSelectionChange}
        />
      ),
    },
    {
      title: "Base Amount",
      key: "baseAmount",
      render: (_: string, record: IBriefInvoice) => (
        <div>{formatData(record.baseAmount || 0)}</div>
      ),
    },
    {
      title: "GST Amount",
      key: "gstAmount",
      render: (_: string, record: IBriefInvoice) => (
        <div>{formatData(record.gstAmount || 0)}</div>
      ),
    },
    {
      title: "Total Amount",
      key: "totalAmount",
      render: (_: string, record: IBriefInvoice) => (
        <div>{formatData(record.totalAmount || 0)}</div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: string, record: IBriefInvoice) => (
        <>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEditClick(record)}
            type="text"
          />
        </>
      ),
    },
  ];

  return (
    <>
        <Table
          columns={columns}
          title={() => <TableTitleLayout 
            filterComponent={
            <div style={{width: "100%"}}>
            <Flex justify="space-between" style={{width: "100%"}}>
              <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{width: 300}}
              placeholder="Search by invoice number or billing ID"
              prefix={<Search size={14}/>}
            />
            <Button
              icon={<PlusOutlined />}
              onClick={() => setCreateModalOpen(true)}
              type="primary"
            >
              Create Invoice
            </Button>
            </Flex>
                          <Divider />

            <Row gutter={16}>
          {aggregation?.all && (
            <Col span={8}>
              <AggregationCard values={aggregation?.all} title="All" />
            </Col>
          )}
          {aggregation?.debits && (
            <Col span={8}>
              <AggregationCard values={aggregation?.debits} title="Debits" />
            </Col>
          )}
          {aggregation?.credits && (
            <Col span={8}>
              <AggregationCard values={aggregation?.credits} title="Credits" />
            </Col>
          )}
        </Row>
            </div>}
            totalText={`${totalCount || 0} Records found`}
          />}
          loading={isFetching}
          dataSource={invoices}
          scroll={{ x: 1000 }}
        />

      <Modal
        title="Create Invoice"
        open={isCreateModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
        }}
        footer={null}
        destroyOnClose
      >
        <CreateBriefInvoiceEntry
          onSubmit={handleCreate}
          isSubmitting={isCreating}
          onCancel={() => setCreateModalOpen(false)}
        />
      </Modal>

      <Modal
        title="Edit Invoice"
        open={isEditModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          setSelectedInvoice(null);
        }}
        footer={null}
        destroyOnClose
      >
        {selectedInvoice && (
          <EditBriefInvoiceEntry
            onSubmit={handleEdit}
            isSubmitting={isEditing}
            onCancel={() => {
              setEditModalOpen(false);
              setSelectedInvoice(null);
            }}
            initialValues={selectedInvoice}
          />
        )}
      </Modal>
    </>
  );
}

export function AggregationCard({
  values,
  title,
}: {
  values: IGetBriefInvoiceAggregation["debits"];
  title: string;
}) {
  return (
    <Card title={title} bordered={false}>
      <Flex vertical>
        <div>Total Amount : {formatData(values?.totalAmount || 0)}</div>
        <div>Gst Amount : {formatData(values?.gstAmount || 0)}</div>
        <div>Base Amount : {formatData(values?.baseAmount || 0)}</div>
      </Flex>
    </Card>
  );
}
